import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Eye, EyeOff } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormGroup,
  InputLabel,
  TextInput,
  ErrorText,
  PasswordInputWrapper,
  PasswordToggleButton,
  ModalCancelButton,
  ModalSubmitButton,
} from './changePasswordModal.styles';
import { changePassword } from '../../../services/auth';

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const overlayRef = useRef(null);

  const mutation = useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      window.alert('Password changed successfully');
      handleClose();
    },
    onError: (err) => {
      setErrors({ form: err?.message || 'Failed to change password. Please try again.' });
    },
  });

  const handleClose = () => {
    setOldPassword('');
    setNewPassword('');
    setShowOldPassword(false);
    setShowNewPassword(false);
    setErrors({});
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const nextErrors = {};
    if (!oldPassword.trim()) nextErrors.oldPassword = 'Old password is required';
    if (!newPassword.trim()) nextErrors.newPassword = 'New password is required';

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    mutation.mutate({ oldPassword, newPassword });
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <ModalOverlay
      onClick={handleClose}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      ref={overlayRef}
    >
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle id="modal-title">Change password</ModalTitle>
          <ModalCloseButton onClick={handleClose} type="button" aria-label="Close">
            <X size={16} />
          </ModalCloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <InputLabel htmlFor="old-password">Old password</InputLabel>
              <PasswordInputWrapper>
                <TextInput
                  id="old-password"
                  type={showOldPassword ? 'text' : 'password'}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder="Enter current password"
                  style={{ paddingRight: '40px' }}
                  autoComplete="current-password"
                  $hasError={!!errors.oldPassword}
                  aria-describedby={errors.oldPassword ? 'old-password-error' : undefined}
                />
                <PasswordToggleButton
                  type="button"
                  onClick={() => setShowOldPassword((v) => !v)}
                  aria-label={showOldPassword ? 'Hide password' : 'Show password'}
                >
                  {showOldPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </PasswordToggleButton>
              </PasswordInputWrapper>
              {errors.oldPassword && (
                <ErrorText id="old-password-error" role="alert">
                  {errors.oldPassword}
                </ErrorText>
              )}
            </FormGroup>

            <FormGroup>
              <InputLabel htmlFor="new-password">New password</InputLabel>
              <PasswordInputWrapper>
                <TextInput
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  style={{ paddingRight: '40px' }}
                  autoComplete="new-password"
                  $hasError={!!errors.newPassword}
                  aria-describedby={errors.newPassword ? 'new-password-error' : undefined}
                />
                <PasswordToggleButton
                  type="button"
                  onClick={() => setShowNewPassword((v) => !v)}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </PasswordToggleButton>
              </PasswordInputWrapper>
              {errors.newPassword && (
                <ErrorText id="new-password-error" role="alert">
                  {errors.newPassword}
                </ErrorText>
              )}
            </FormGroup>

            {errors.form && <ErrorText role="alert">{errors.form}</ErrorText>}
          </ModalBody>

          <ModalFooter>
            <ModalCancelButton type="button" onClick={handleClose}>
              Cancel
            </ModalCancelButton>
            <ModalSubmitButton type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              ) : null}
              {mutation.isPending ? 'Updating…' : 'Update password'}
            </ModalSubmitButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default ChangePasswordModal;
